<template>
    <div class="action-plans-table" :class="$mq">
        <BaseTable v-if="hasActionPlans" :headers="translatedActionPlansHeaders" :rows="sortedActionPlansRows" :totalItems="getTotalPendingActionPlans" :pageNumber="getPageNumber" :itemsPerPage="getItemsPerPage" :columnStyles="columnStyles" :sortColumn="sortColumn" :sortDirection="sortDirection" @row-click="(row) => goToActionPlan(row)" @back-button-click="goToPreviousPage" @forward-button-click="goToNextPage" @sort-change="handleSortChange">
            <template #cell-status="{ value }">
                <div class="action-plan-status">
                    <ColorAndTextTag v-if="getStatusData(value) !== null" :circleColor="getStatusData(value).color" :text="getStatusData(value).name.toUpperCase()" />
                    <span v-else class="status-placeholder"> - </span>
                </div>
            </template>
            <template #cell-nonConformity="{ value, row }">
                <div :class="{ 'mobile-non-conformity-cell': isCompactLayout }">
                    <ColorAndTextTag v-if="isCompactLayout && getStatusData(row.status) !== null" :circleColor="getStatusData(row.status).color" :text="getStatusData(row.status).name.toUpperCase()" />
                    <span v-else-if="isCompactLayout && getStatusData(row.status) === null" class="status-placeholder"> - </span>
                    <span :class="{ 'mobile-non-conformity': isCompactLayout }">{{ value }}</span>
                </div>
            </template>
        </BaseTable>
        <EmptyPage v-else-if="getHasApiError" :type="'error'" :title="$t('empty_pages.api_error_title')" :description="$t('empty_pages.api_error_description')" />
        <EmptyPage v-else-if="hasActiveSearch && !hasActionPlans" :title="$t('empty_pages.no_matches_title')" :description="$t('empty_pages.no_results_description_action_plans')" />
        <EmptyPage v-else :title="$t('empty_pages.generic_title')" :description="$t('empty_pages.action_plans_description')" />
    </div>
</template>

<script>
import { formatPercent, percentLabel } from '@/utils/numberFormat'
import { formatDisplayDateFromStore } from '@/utils/dateFormatters'
import BaseTable from '@/components/domain/ui/BaseTable.vue'
import ColorAndTextTag from '@/components/domain/ui/ColorAndTextTag.vue'
import EmptyPage from '@/components/layout/EmptyPage.vue'

export default {
    name: 'ActionPlansTable',
    components: { BaseTable, ColorAndTextTag, EmptyPage },
    props: {
        search: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            sortColumn: 'createdDate',
            sortDirection: 'desc',
            auditTemplateId: null
        }
    },
    created() {
        this.auditTemplateId = this.$route.params.id
        this.loadSortPreferences()
    },
    computed: {
        isCompactLayout() {
            return ['portrait', 'landscape'].includes(this.$mq)
        },
        getActionPlansData() {
            return this.$store.getters['actionplan/getActionPlans']
        },
        getTotalPendingActionPlans() {
            return this.$store.getters['actionplan/getTotalPendingActionPlans']
        },
        getActionPlansHeaders() {
            return this.$store.getters['actionplan/getActionPlansHeaders']
        },
        translatedActionPlansHeaders() {
            const headers = this.isCompactLayout
                ? this.getActionPlansHeaders.filter((header) => ['auditDisplayId', 'deadlineDate', 'nonConformity'].includes(header.key))
                : this.getActionPlansHeaders

            return headers.map((header) => {
                return {
                    ...header,
                    text: this.isCompactLayout && header.key === 'auditDisplayId'
                        ? 'ID'
                        : this.percentLabel(this.$t(header.text)).toUpperCase()
                }
            })
        },
        getPageNumber() {
            return this.$store.getters['actionplan/getPageNumber']
        },
        getItemsPerPage() {
            return this.$store.getters['actionplan/getItemsPerPage']
        },
        getIsLoading() {
            return this.$store.getters['actionplan/getIsLoading']
        },
        getHasApiError() {
            return this.$store.getters['actionplan/getHasApiError']
        },
        actionPlansRows() {
            return Object.values(this.getActionPlansData).map((actionPlan, keyIndex) => {
                return {
                    id: actionPlan.id || `action-plan-${keyIndex}`,
                    id_checklist: actionPlan.id_checklist || null,
                    auditDisplayId: actionPlan.issue_id ? this.formatAuditDisplayId(actionPlan.issue_id) : '-',
                    createdDate: actionPlan.created_date ? this.formatDate(actionPlan.created_date) : '-',
                    deadlineDate: actionPlan.end_date ? this.formatDate(actionPlan.end_date) : '-',
                    status: actionPlan.status ? actionPlan.status : '-',
                    progressPercentage: actionPlan.score.toString() ? this.formatProgressPercentage(actionPlan.score.toString()) : '-',
                    nonConformity: actionPlan.audit_item_name ? actionPlan.audit_item_name : '-',
                    rawIssueId: actionPlan.issue_id ? actionPlan.issue_id.toString() : '',
                    storeIndex: keyIndex
                }
            })
        },
        filteredActionPlansRows() {
            // Si no hay búsqueda o tiene menos de 3 caracteres, retornar todas las filas
            if (!this.search || this.search.trim().length < 3) {
                return this.actionPlansRows
            }

            const searchNormalized = this.normalizeText(this.search.toLowerCase().trim())

            return this.actionPlansRows.filter((row) => {
                // Buscar en nombre de no conformidad
                const nonConformityText = row.nonConformity !== '-' ? this.normalizeText(row.nonConformity.toLowerCase()) : ''
                const nonConformityMatch = nonConformityText.includes(searchNormalized)

                // Buscar en número de Auditoría ID (sin el símbolo #)
                const auditIdText = row.rawIssueId ? this.normalizeText(row.rawIssueId.toLowerCase()) : ''
                const auditIdMatch = auditIdText.includes(searchNormalized)

                return nonConformityMatch || auditIdMatch
            })
        },
        sortedActionPlansRows() {
            if (!this.sortColumn) return this.filteredActionPlansRows

            const storeData = Object.values(this.getActionPlansData)

            // Create sorting data directly from filtered rows
            const sortingData = this.filteredActionPlansRows.map((row, index) => {
                // Acceder directamente usando el índice guardado
                const originalActionPlan = storeData[row.storeIndex]

                return {
                    index: index,
                    auditDisplayId: originalActionPlan && originalActionPlan.issue_id ? parseInt(originalActionPlan.issue_id) || 0 : 0,
                    createdDate: originalActionPlan && originalActionPlan.created_date ? originalActionPlan.created_date : 0,
                    deadlineDate: originalActionPlan && originalActionPlan.end_date ? originalActionPlan.end_date : 0,
                    status: originalActionPlan && originalActionPlan.status ? originalActionPlan.status : '',
                    progressPercentage: originalActionPlan && originalActionPlan.score ? originalActionPlan.score : 0,
                    nonConformity: originalActionPlan && originalActionPlan.audit_item_name ? originalActionPlan.audit_item_name : ''
                }
            })

            // Sort the data
            const sorted = sortingData.sort((a, b) => {
                let aValue = a[this.sortColumn]
                let bValue = b[this.sortColumn]

                // Handle null/undefined values
                if (aValue === null || aValue === undefined) aValue = ''
                if (bValue === null || bValue === undefined) bValue = ''

                // Compare values
                let comparison = 0
                if (aValue < bValue) {
                    comparison = -1
                } else if (aValue > bValue) {
                    comparison = 1
                }

                return this.sortDirection === 'desc' ? -comparison : comparison
            })

            // Return the filtered rows in the new order
            return sorted.map((item) => this.filteredActionPlansRows[item.index])
        },
        columnStyles() {
            return {
                deadlineDate: 'bold-text'
            }
        },
        getAccountActionPlansStatuses() {
            return this.$store.getters['actionplan/getAccountActionPlansStatuses']
        },
        hasActionPlans() {
            return this.sortedActionPlansRows && this.sortedActionPlansRows.length > 0
        },
        hasActiveSearch() {
            return this.search && this.search.trim().length >= 3
        }
    },
    methods: {
        formatDisplayPercent(value, level = 1, allowNegative = false) { return formatPercent(value, this.$i18n.locale, level, { allowNegative }) },
        percentLabel,
        /**
         * Normaliza texto eliminando tildes y acentos
         * @param {string} text - Texto a normalizar
         * @returns {string} Texto normalizado sin tildes
         */
        normalizeText(text) {
            if (!text || typeof text !== 'string') {
                return ''
            }
            return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        },
        goToPreviousPage() {
            this.$store.commit('actionplan/setPageNumber', this.getPageNumber - 1)
        },
        goToNextPage() {
            this.$store.commit('actionplan/setPageNumber', this.getPageNumber + 1)
        },
        formatAuditDisplayId(number) {
            if (!number) {
                return '-'
            }
            const num = parseInt(number)
            if (isNaN(num)) {
                return `#${number}`
            }
            return `#${num < 10 ? '0' + num : num}`
        },
        formatProgressPercentage(progressPercentage) {
            if (progressPercentage == null || progressPercentage === '') {
                return '-'
            }
            return this.formatDisplayPercent(progressPercentage)
        },
        formatDate(timestamp) {
            return formatDisplayDateFromStore(timestamp)
        },
        goToActionPlan(row) {
            const actionPlanId = row.id_checklist
            const currentPageNumber = this.getPageNumber

            this.$router.push({
                name: 'ActionPlan',
                params: {
                    id: actionPlanId,
                    auditTemplateId: this.$route.params.id,
                    pageNumber: currentPageNumber
                }
            })
        },
        getStatusData(statusId) {
            if (!statusId || statusId === '-') {
                return null
            }
            const status = this.getAccountActionPlansStatuses.find((statusData) => statusData.id === statusId)
            if (!status) {
                return null
            }

            const hasValidColor = status.color && typeof status.color === 'string' && status.color.trim() !== ''
            const hasValidName = status.name && typeof status.name === 'string' && status.name.trim() !== ''

            if (!hasValidColor || !hasValidName) {
                return null
            }

            return {
                color: status.color.trim(),
                name: status.name.trim()
            }
        },
        handleSortChange({ column, direction }) {
            this.sortColumn = column
            this.sortDirection = direction
            this.saveSortPreferences()
        },
        loadSortPreferences() {
            const savedSortColumn = localStorage.getItem('actionPlansTableSortColumn')
            const savedSortDirection = localStorage.getItem('actionPlansTableSortDirection')

            if (savedSortColumn) {
                this.sortColumn = savedSortColumn
            }
            if (savedSortDirection) {
                this.sortDirection = savedSortDirection
            }
        },
        saveSortPreferences() {
            localStorage.setItem('actionPlansTableSortColumn', this.sortColumn)
            localStorage.setItem('actionPlansTableSortDirection', this.sortDirection)
        }
    },
    watch: {
        getPageNumber(newVal, oldVal) {
            if (newVal !== oldVal && this.auditTemplateId && this.$route.name === 'AuditDetail') {
                this.$store.dispatch('actionplan/loadActionPlansData', { auditTemplateId: this.auditTemplateId })
            }
        },
        getIsLoading(newVal) {
            if (newVal) {
                this.$overlay.loading()
            } else {
                this.$overlay.hide()
            }
        }
    },
    mounted() {},
    beforeDestroy() {
        this.$store.commit('actionplan/setPageNumber', 1)
        this.auditTemplateId = null
    }
}
</script>

<style lang="scss" scoped>
.action-plans-table {
    width: 100%;
    height: calc(100% - 116px);
    background-color: $default-bg-color;
    overflow: hidden;
}

.status-placeholder {
    color: $color-black;
}

.action-plan-status {
    min-width: 0;

    ::v-deep .tag-text {
        max-width: none;
    }
}

.action-plans-table.portrait,
.action-plans-table.landscape {
    ::v-deep .table-wrapper {
        overflow-x: hidden;
    }

    ::v-deep .base-table {
        table-layout: fixed;

        .headers th,
        .body .cell {
            min-width: 0;
            max-width: none;
        }

        .headers th {
            padding: 4px;
            font-size: 12px;
        }

        .body .cell {
            padding: 8px 4px;
        }

        th:nth-child(1),
        td:nth-child(1) {
            width: 15%;
        }

        th:nth-child(2),
        td:nth-child(2) {
            width: 37%;
        }

        th:nth-child(3),
        td:nth-child(3) {
            width: 48%;
        }
    }

    .mobile-non-conformity-cell {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        width: 100%;
        min-width: 0;

        ::v-deep .color-text-tag {
            width: auto;
            max-width: 100%;
            min-width: 0;
            height: auto;
            min-height: 25px;
            padding: 3px 8px;
            gap: 6px;
        }

        ::v-deep .tag-text {
            max-width: none;
            white-space: normal;
            overflow: visible;
            text-overflow: clip;
            overflow-wrap: anywhere;
            line-height: 16px;
        }
    }

    .mobile-non-conformity {
        display: -webkit-box;
        overflow: hidden;
        overflow-wrap: anywhere;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
    }
}
</style>
